import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { meetingMediaUrl } from "../../api/Meetings/meetingSessionsApi";
import {
  meetingMediaThumbFrameSx,
  meetingMediaThumbMediaSx,
} from "./meetingUiStyles";

/**
 * Miniatura de vídeo: carga el primer fotograma solo al entrar en viewport
 * (preload metadata + #t=0.1). Caja 4:3 fija como las fotos.
 * @param {boolean} [eager] — si true, carga al montar (p. ej. dentro de un Dialog).
 */
export default function MeetingVideoThumb({
  fileUrl,
  sx,
  eager = false,
  placeholder = false,
}) {
  const rootRef = useRef(null);
  const [active, setActive] = useState(Boolean(eager));

  useEffect(() => {
    if (placeholder) return undefined;
    if (eager) {
      setActive(true);
      return undefined;
    }
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setActive(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActive(true);
          io.disconnect();
        }
      },
      { rootMargin: "160px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [eager, placeholder]);

  const src =
    placeholder || !active
      ? undefined
      : `${meetingMediaUrl(fileUrl)}${String(fileUrl || "").includes("#") ? "" : "#t=0.1"}`;

  return (
    <Box
      ref={rootRef}
      sx={{
        ...meetingMediaThumbFrameSx,
        minHeight: 0,
        ...sx,
      }}
    >
      {src ? (
        <Box
          component="video"
          src={src}
          muted
          playsInline
          preload="metadata"
          onLoadedData={(e) => {
            try {
              const v = e.currentTarget;
              if (v && v.currentTime < 0.05) v.currentTime = 0.1;
            } catch {
              /* ignore seek */
            }
          }}
          sx={{
            ...meetingMediaThumbMediaSx,
            pointerEvents: "none",
          }}
        />
      ) : (
        <Box
          sx={{
            ...meetingMediaThumbMediaSx,
            bgcolor: "grey.800",
          }}
        />
      )}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          pointerEvents: "none",
          zIndex: 1,
        }}
        aria-hidden
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "rgba(0,0,0,0.45)",
            color: "common.white",
            display: "grid",
            placeItems: "center",
            fontSize: 11,
            fontWeight: 700,
            pl: 0.25,
          }}
        >
          ▶
        </Box>
      </Box>
    </Box>
  );
}
